/**
 * Carga da Maps JavaScript API para o mini mapa da cotação (F-112), pelo
 * carregador oficial (`@googlemaps/js-api-loader`, que injeta o bootstrap
 * documentado pelo Google). Nada é carregado até o diálogo abrir com uma chave
 * do navegador; sem chave, este módulo nem é chamado.
 *
 * Três jeitos de não haver mapa, todos tratados igual pelo diálogo ("sem
 * mapa", uma linha discreta, e a cotação segue):
 *
 * - o script não carrega (rede, bloqueador, CSP): `importLibrary` rejeita;
 * - não carrega a tempo: o prazo abaixo rejeita;
 * - a chave é recusada (referrer fora da lista — o caso do localhost —, chave
 *   inválida, API não habilitada): o script CARREGA, o mapa até nasce, e o
 *   Google chama `window.gm_authFailure` e cobre o mapa com um aviso de erro.
 *   Quem estiver inscrito aqui recebe o aviso e tira o mapa da tela.
 *
 * O `setOptions` do carregador só vale uma vez por página: a chave do
 * navegador é uma só, e uma segunda chave na mesma página é recusada.
 */
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

export interface GoogleMapsLibs {
  core: google.maps.CoreLibrary;
  maps: google.maps.MapsLibrary;
  marker: google.maps.MarkerLibrary;
}

/** quanto esperar o script antes de desistir do mapa (a cotação não espera) */
export const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 15_000;

declare global {
  interface Window {
    /** o Google chama quando a chave é recusada (documentado em "Error messages") */
    gm_authFailure?: () => void;
  }
}

let optionsKey: string | null = null;
let pending: Promise<GoogleMapsLibs> | null = null;
let authFailed = false;
const authListeners = new Set<() => void>();

function installAuthFailureHook() {
  const previous = window.gm_authFailure;
  window.gm_authFailure = () => {
    authFailed = true;
    for (const listener of authListeners) listener();
    previous?.();
  };
}

/** a chave foi recusada pelo Google (nesta página, para sempre) */
export function onGoogleMapsAuthFailure(listener: () => void): () => void {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

export function loadGoogleMaps(key: string): Promise<GoogleMapsLibs> {
  if (authFailed) return Promise.reject(new Error('google maps: chave recusada'));
  if (optionsKey !== null && optionsKey !== key) {
    return Promise.reject(new Error('google maps: outra chave já carregada nesta página'));
  }
  if (pending) return pending;
  if (optionsKey === null) {
    installAuthFailureHook();
    // a chave vai na URL do script — é assim que a Maps JavaScript API a
    // recebe, e é por isso que ela é restrita por referrer e por API
    setOptions({ key, v: 'weekly', language: 'pt-BR', region: 'BR' });
    optionsKey = key;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('google maps: não carregou a tempo')),
      GOOGLE_MAPS_LOAD_TIMEOUT_MS
    );
  });
  const load = Promise.all([
    importLibrary('core'),
    importLibrary('maps'),
    importLibrary('marker'),
  ]).then(([core, maps, marker]) => ({ core, maps, marker }));
  const attempt = Promise.race([load, timeout]).finally(() => clearTimeout(timer));
  pending = attempt;
  // falhou: a próxima abertura do diálogo tenta de novo (o bootstrap do Google
  // também esquece a tentativa que falhou)
  attempt.catch(() => {
    if (pending === attempt) pending = null;
  });
  return attempt;
}
